import { TAB_INDEX_FOCUSABLE_TAB_SEQUENCE } from "@multiweb/reactUtils";
import { Key } from "Global/Base/Utils/EventUtils";
import { StringUtils } from "Global/Base/Utils/StringUtils";
import { CancellationTokenSource } from "../../../../../Global/Base/Promises/CancellationTokenSource";
import { PromiseUtils } from "../../../../../Global/Base/Promises/PromiseUtils";
import { Localization } from "../../../../../OW.Remoting/Remoting/Localization";
import { Control } from "../../../../UI.BaseControls/Control";
import { Action } from "../../../../UI.Controls/Actions/Action";
import { DropDownMenuHorizontalOpen, IDropDownMenu } from "../../../../UI.Controls/DropDownMenu/DropDownInterfaces";
import { DropDownMenu } from "../../../../UI.Controls/DropDownMenu/DropDownMenu";
import { DropDownMenuItem } from "../../../../UI.Controls/DropDownMenu/DropDownMenuItem";
import { DropDownTextEditItem } from "../../../../UI.Controls/DropDownMenu/DropDownTextEditItem";
import { SeparatorMenuItem } from "../../../../UI.Controls/DropDownMenu/SeparatorItem";
import { registerCustomElement } from "../../../../UI.ElementarControls/Utils/CustomElementUtils";
import { DropDownMenuCaptionItem } from "./DropDownMenuItems/DropDownMenuCaptionItem";
import { DropDownMenuCheckboxItem } from "./DropDownMenuItems/DropDownMenuCheckboxItem";
import { IFilterValue, IMultiOneOperation, IMultiOperationValue, IMultiOperationValueList, IOperation } from "./FilterInterfaces";
import { logger } from "./logger";

export class FilterCombo extends Control {
    public defaultLocKey: string;
    private _dropDownMenu: DropDownMenu;
    private _canUseFilter: boolean;
    private _lastValue: string;
    private _lastItem: DropDownMenuItem;

    private _lastSelectedItem: DropDownMenuItem;
    private _canSelectItem: boolean;

    private _filterMenuElement: DropDownTextEditItem; // menu obsahujicí položky komba
    private _textDiv: HTMLElement; // text vedle šipky
    private _arrow: HTMLElement; // šipka komba

    private _lastArrowSelected: DropDownMenuItem;
    private _lastIconName: string;

    private _closeListener: (sender: DropDownMenu, prm: any) => void;
    private _comboItems: DropDownMenuItem[];
    private _parentMenu: IDropDownMenu;
    private _filterValueChangeCts: CancellationTokenSource;

    /**
     * Indikuje zda v menu bude použit filtrační řádek
     */
    public get canUseFilter(): boolean {
        return this._canUseFilter;
    }

    public set canUseFilter(v: boolean) {
        this._canUseFilter = v;

        this._filterMenuElement.visible = v;
    }

    /** Pokud je true, tak se při kliku na danou položku dojde k jejímu označení */
    public get canSelectItem(): boolean {
        return this._canSelectItem;
    }

    public set canSelectItem(v: boolean) {
        if (v === false) {
            this._deselectItem();
        }
        this._canSelectItem = v;
    }

    public set parentMenu(menu: IDropDownMenu) {
        this._parentMenu = menu;
    }

    /**
     * Rozparsuje menu na základě předané struktury
     */
    public parseFromStructure(struct: IFilterValue, selectedOper?: IOperation | IMultiOperationValueList): void {
        // temp funkce pro vytvoření položky menu. Kde při kliku na položk je vyvolána nativní akce ItemSelected
        const f_createMenuItem = (cList: IOperation) => {
            let element: DropDownMenuItem;
            if (cList.captionLocKey) {
                element = new DropDownMenuCaptionItem();
            } else {
                element = new DropDownMenuItem();
            }
            const action = new Action(async () => {
                if (this._parentMenu && !this._parentMenu.visible)
                    this._parentMenu.visible = true;
                this.callEvent("ItemSelected", {
                    operation: cList,
                    element: element,
                });
                //#region Nastaveni polozky jako vybrane
                this.selectItem(element);
                if (cList.fieldList.length == 0)
                    this.callEvent("filterExecuteRequest");
                //#endregion Nastaveni polozky jako vybrane
            }, cList.lockey);

            element.action = action;
            action.enabled = true;

            element.icon = "None";

            if (cList.captionLocKey) {
                (element as DropDownMenuCaptionItem).setCaptionsLocKey(cList.captionLocKey, cList.lockey);
            }

            return element;
        };

        for (const gList of struct.groupList) {
            if (gList.visible === false)
                continue;

            const visibleOperations = gList.operationList.filter((operation) => operation.isVisible);

            if (this._dropDownMenu.itemsCount > 1 && visibleOperations.length > 0) { // Menu obsahuje vždy minimálně filtrovací pole
                this._dropDownMenu.addItem(new SeparatorMenuItem());
            }

            for (const operation of visibleOperations) {
                const tempItem = f_createMenuItem(operation);

                this._dropDownMenu.addItem(tempItem);
                this._comboItems.push(tempItem);

                if (selectedOper === operation) { // Vybrani vychozi polozky
                    this.selectItem(tempItem);
                }
            }
        }

        if (struct.multiOperation) {
            //const ft = Structure.GetField(struct.multiOperation.valueListType);
            //const af = new ft.control();
            //this.LoadMultiItems(af);
            const f_createAction = (it: IMultiOperationValue, el: DropDownMenuCheckboxItem) => {
                return async () => {
                    el.isChecked = !el.isChecked;
                    this.callEvent(el.isChecked ? "ItemChecked" : "ItemUnchecked",
                        {
                            operation: it,
                            element: el,
                        });
                };
            };

            const f_createChboxItem = (item: IMultiOneOperation, dataType: string) => {
                const m = new DropDownMenuCheckboxItem();

                if (item.captionHtml) {
                    m.setCaption(item.captionHtml, item.lockey);
                    (m.action as Action).action = f_createAction({
                        value: item.value,
                        id: item.id,
                        fieldType: dataType,
                        displayValue: item.captionHtml,
                    }, m);
                } else {
                    m.setLocalizedCaption(item.lockey);
                    m.locKey = item.lockey;
                    (m.action as Action).action = f_createAction({
                        value: item.value,
                        id: item.id,
                        fieldType: dataType,
                        displayValue: "",
                    }, m);
                }

                m.isChecked = item.isDefault || false;
                m.value = item.value;
                m.valueType = dataType;
                m.valueId = item.id;
                m.action.enabled = true;

                return m;
            };
            this.setValueAsIcon("FilterMultiItem");

            let inserted = false;

            for (const gList of struct.multiOperation.groupList) {
                if (gList.visible === false)
                    continue;
                if (inserted && gList.operationList.length > 0)
                    this._dropDownMenu.addItem(new SeparatorMenuItem());

                for (const cList of gList.operationList) {
                    if (!cList.isVisible)
                        continue;
                    const tempItem = f_createChboxItem(cList, struct.dataType);
                    this._dropDownMenu.addItem(tempItem);
                    this._comboItems.push(tempItem);

                    inserted = true;
                }
            }
        }
    }

    public addItem(locKey: string, action: () => Promise<void>, locParams?: string[]): void {
        this._dropDownMenu.addItem(DropDownMenuItem.createDropMenuItem("none", action, locKey, true, undefined, locParams));
    }

    public clearItems(): void {
        this._dropDownMenu.clearItems();

        this._dropDownMenu.addItem(this._filterMenuElement);
    }

    public getItem(oper: IMultiOperationValue): DropDownMenuCheckboxItem {
        for (const item of this._comboItems) {
            if (item instanceof DropDownMenuCheckboxItem) {
                const chckboxitem = item as DropDownMenuCheckboxItem;
                if ((chckboxitem.value == oper.value) && (chckboxitem.valueType == oper.fieldType) && (chckboxitem.valueId == oper.id))
                    return chckboxitem;
            }
        }
        return null;
    }

    /**
     * Označí položku jako vybranou
     * @param item Položka menu k označení
     */
    public selectItem(item: DropDownMenuItem): void {
        if (!this.canSelectItem) {
            return;
        }

        this._deselectItem();

        this._lastSelectedItem = item;

        if (item) {
            this._lastSelectedItem.classList.add("selected");
            this._lastSelectedItem.icon = "Checked";
        }
    }

    /** Zruší označení položky */
    private _deselectItem(): void {
        if (this._lastSelectedItem) {
            this._lastSelectedItem.classList.remove("selected");
            this._lastSelectedItem.icon = "None";
            this._lastSelectedItem = null;
        }
    }

    /**
     * Nastaví textovou hodnotu komba
     * @param locKey
     */
    public setValue(value: string | DropDownMenuItem): void {
        if (this._lastItem) {
            this._lastItem.classList.remove("selected");
            this._lastItem.icon = "None";
        }
        if (this._lastIconName)
            this._textDiv.classList.remove("Icon", this._lastIconName);
        if (typeof value === "string") {
            const locKey = value as string;
            this._lastValue = locKey;
            Localization.getTextPromise(locKey).then((locItem) => {
                if (locItem.trim().length == 0)
                    this._textDiv.style.display = "none";
                else
                    this._textDiv.style.display = "";

                this._textDiv.innerHTML = `<span>${StringUtils.stringToHTMLText(locItem)}</span>`;
            }).catch((e) => logger.error(e));

            //const items = Array.from(this.dropDownMenu.querySelectorAll(".DropDownMenuItem")) as DropDownMenuItem[];

            //for (let item of items) {
            //    if (item.LocKey == locKey) {
            //        this.lastItem = item;
            //        this.lastItem.classList.add("selected");
            //        this.lastItem.Icon = "Checked";
            //        break;
            //    }
            //}
        } else if (value instanceof DropDownMenuItem) {
            const val = value as DropDownMenuItem;
            this._lastItem = val;
            this._lastItem.classList.add("selected");
            this._lastItem.icon = "Checked";

            Localization.getTextPromise(this._lastItem.locKey).then((locItem) => {
                if (locItem.trim().length == 0)
                    this._textDiv.style.display = "none";
                else
                    this._textDiv.style.display = "";
                this._textDiv.innerHTML = `<span>${StringUtils.stringToHTMLText(locItem)}</span>`;
            }).catch((e) => logger.error(e));
        } else if (value == undefined) {
            this._lastValue = "";
            this._lastItem = null;
        }
    }

    public setValueAsIcon(iconName: string): void {
        if (iconName) {
            this._textDiv.innerHTML = "";
            this._textDiv.classList.add("Icon", iconName);
            this._textDiv.style.display = "";
            if (this._lastIconName)
                this._textDiv.classList.remove(this._lastIconName);
            this._lastIconName = iconName;
        } else {
            this._textDiv.style.display = "none";
        }
    }

    protected override _constructor(): void {
        super._constructor();

        this._canUseFilter = true;
        this.classList.add("FilterCombo", "btn", "flat", "is-dropdown");
        this.tabIndex = TAB_INDEX_FOCUSABLE_TAB_SEQUENCE;
        this._dropDownMenu = new DropDownMenu();
        this._dropDownMenu.horizontalOpenPosition = DropDownMenuHorizontalOpen.left;
        this._textDiv = document.createElement("div");
        this._arrow = document.createElement("div");

        this._textDiv.classList.add("text");
        // this.arrow.classList.add("arrow");

        this.appendChild(this._textDiv);
        this.appendChild(this._arrow);

        this._filterMenuElement = new DropDownTextEditItem();
        this._comboItems = [];
        this._dropDownMenu.addItem(this._filterMenuElement);
    }

    private _nextItem(): void {
        let next: Element = null;
        if (this._lastArrowSelected) {
            next = (this._lastArrowSelected as HTMLElement);
            do {
                next = next.nextElementSibling;
                if (next instanceof SeparatorMenuItem || next instanceof DropDownTextEditItem)
                    continue;
                else if (next != null && (next as Control).visible)
                    break;
            } while (next != null);
            if (next == null) {
                next = this._dropDownMenu.querySelector(".DropDownMenuItem:not(.TextItem):not(.hidden)");
            }
        } else {
            next = this._dropDownMenu.querySelector(".DropDownMenuItem:not(.TextItem):not(.hidden)");
        }
        if (!next)
            return;

        if (this._lastArrowSelected)
            this._lastArrowSelected.classList.remove("selected");
        next.classList.add("selected");
        this._lastArrowSelected = next as DropDownMenuItem;
    }

    private _prevItem(): void {
        let prev: Element = null;
        if (this._lastArrowSelected) {
            prev = (this._lastArrowSelected as HTMLElement);
            do {
                prev = prev.previousElementSibling;

                if (prev instanceof SeparatorMenuItem || prev instanceof DropDownTextEditItem)
                    continue;
                else if (prev != null && (prev as Control).visible)
                    break;
            } while (prev != null);
            if (prev == null) {
                const all = this._dropDownMenu.querySelectorAll(".DropDownMenuItem:not(.TextItem):not(.hidden)");
                prev = all.length > 0 ? all[all.length - 1] : null; //this.dropDownMenu.querySelector(".DropDownMenuItem:not(.TextItem):last-child");
            }
        } else {
            //prev = this.dropDownMenu.querySelector(".DropDownMenuItem:not(.TextItem):last-child");
            const all = this._dropDownMenu.querySelectorAll(".DropDownMenuItem:not(.TextItem):not(.hidden)");
            prev = all.length > 0 ? all[all.length - 1] : null;
        }
        if (!prev)
            return;

        if (this._lastArrowSelected)
            this._lastArrowSelected.classList.remove("selected");
        prev.classList.add("selected");
        this._lastArrowSelected = prev as DropDownMenuItem;
    }

    // You can also define the other lifecycle methods.
    protected override connectedCallback(): void {
        super.connectedCallback();
        if (this._lastValue) {
            this.setValue(this._lastValue);
        } else if (this.defaultLocKey) {
            Localization.addLocData(this._textDiv, this.defaultLocKey);
        } else {
            if (!this._lastIconName)
                this._textDiv.style.display = "none";
        }

        this._registerListeners();
    }

    private _registerListeners(): void {
        this._dropDownMenu.onClose.add(this._closeListener = () => this.classList.remove("opened"));
        this._dropDownMenu.registerEventListener("mousemove", () => this._lastArrowSelected?.classList.remove("selected"));
        this._dropDownMenu.registerEventListener("mouseleave", () => this._lastArrowSelected?.classList.add("selected"));
        this.registerEventListener("click", (event) => this._onClickListener(event));
    }

    private _onClickListener(clickEvent: Event): void {
        if (this.classList.contains("Disabled"))
            return;

        if (this._dropDownMenu.isOpened) {
            this._dropDownMenu.closeAll();
            this.classList.remove("opened");

            if (this.canUseFilter) {
                this._filterMenuElement.focus();
            }
        } else {
            if (this._dropDownMenu.itemsCount === 2) { // Skrytý input pro posun v menu šipkami + jedna položka menu
                return;
            }

            this._dropDownMenu.show(this, clickEvent);
            this._filterMenuElement.focus();
            this._filterMenuElement.editElement.registerEventListener("keyup", (event: KeyboardEvent) => this._onKeyUpListener(event));

            // je potreba toto menu nastavit rodicovskemu jako nove otevrene - jinak by se rodicovske mohlo zavrit pri kliku do tohoto (kontroluje se InElement() na sobe a na openedSubmenu)
            if (this._parentMenu != null) {
                if (this._parentMenu.openedSubmenu && this._parentMenu.openedSubmenu != this._dropDownMenu)
                    this._parentMenu.openedSubmenu.closeAll();
                this._parentMenu.openedSubmenu = this._dropDownMenu;
            }
            this.classList.add("opened");
        }
    }

    private _onKeyUpListener(e: KeyboardEvent): void {
        if (e.key === Key.Escape) {
            this._dropDownMenu.clearFilter();
            this._filterMenuElement.editElement.value = "";
            return;
        } else if (e.key === Key.ArrowDown || e.key === Key.ArrowUp) {
            if (e.key === Key.ArrowDown)
                this._nextItem();

            if (e.key === Key.ArrowUp)
                this._prevItem();

            return;
        } else if (e.key === Key.Enter) {
            e.stopPropagation();

            if (this._lastArrowSelected) {
                if (this._lastArrowSelected.classList.contains("selected")) {
                    if (e.shiftKey) { // pokud s entrem zmackneme i shift, provede se vybrani akce, ale nezavre se okno
                        (this._lastArrowSelected.action as Action).execute();
                        this._filterMenuElement.focus();
                        return;
                    } else {
                        this._lastArrowSelected.click();
                        this._lastArrowSelected.classList.remove("selected");
                    }
                } else {
                    const elem = this._dropDownMenu.querySelector(":not(.TextItem).DropDownMenuItem:hover") as HTMLElement;
                    elem.click();
                }

                this._lastArrowSelected = null;
            }

            return;
        }

        const { promise, cts } = PromiseUtils.cancelPrevAndDelayCall(this._filterValueChangeCts, 300, () => this._onChangeFilterValue());
        this._filterValueChangeCts = cts;
        promise.catch(PromiseUtils.cancellationCatch);
    }

    private _onChangeFilterValue(): void {
        const val = this._filterMenuElement.editElement.value.trim();

        if (val.length > 0)
            this._dropDownMenu.filter(val);
        else
            this._dropDownMenu.clearFilter();

        // pokud po vyfiltrování nezůstane viditělna polozka, ktera byla vybrana sipkama zrusi se
        if (this._lastArrowSelected && !this._lastArrowSelected.visible) {
            this._lastArrowSelected.classList.remove("selected");
            this._lastArrowSelected = null;
        }

        // jelikož se změní počet filtrovaných položek menu, přepočítají se jeho parametry
        this._dropDownMenu.isOverflow = false;
        this._dropDownMenu.recalculatePosition();
    }

    protected override disconnectedCallback(): void {
        super.disconnectedCallback();

        this._dropDownMenu.onClose.remove(this._closeListener);
        this._dropDownMenu.unregisterEventListener("mousemove");
        this._dropDownMenu.unregisterEventListener("mouseleave");
        this.unregisterEventListener("click");
        this._filterMenuElement.editElement.unregisterEventListener("keyup");
    }
}

registerCustomElement("tsc-filtercombo", FilterCombo);
